import { IdParams } from '../dto/common.js';
import { DocIdParams } from '../dto/documents.js';
import { KnowledgeCreateV1, KnowledgeEventV1, PlayCreateV1, PlayRecordV1, SecretsQueryV1, SecretV1, SessionCreateV1, SessionUpdateV1, SessionV1, } from '../dto/ledger.js';
import { listOf, PageQueryV1 } from '../dto/list.js';
import { defineRoute } from '../route/define-route.js';
import { HEADER_FROM_1M1_S3, HEADER_FROM_2C_S3 } from './change-batch-senders.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_KNOWLEDGE_CREATED, EXAMPLE_KNOWLEDGE_RETRACTED, EXAMPLE_PLAY_CREATED, EXAMPLE_PLAY_RECORD, EXAMPLE_SECRET, EXAMPLE_SESSION, EXAMPLE_SESSION_CREATED, EXAMPLE_SESSION_PLAYED, EXAMPLE_SESSION_UPDATED, } from './examples-ledger.js';
/**
 * The ledger routes of exit 1b (1.0b deliverable 19): secrets, knowledge events and play records
 * (1.M-1), and sessions (1.T). Records are written against a document, never a reference one.
 * The three ledger writes name their change batch in `CHANGE_BATCH_HEADER` from 1.M-1 s3 (CS r5b
 * deliverable 162; marked under the orchestrator's consent on CS's behalf, DECISIONS 354).
 */
const ids = EXAMPLE_IDS;
const ledger = { auth: 'session', status: 'frozen', since: 'exit-1b', owner: '1.M-1' };
const sessions = { ...ledger, owner: '1.T' };
/**
 * The ledger writes' refusals (CS r2b; 1.M-1 deliverable 13 request 2): a trashed subject or session
 * document answers 409 `document_trashed`; a write the schema admits but the rows refuse (1.M-1
 * deliverable 9's checks) answers 422 `invalid_body` with `details.issue`.
 */
const ledgerWrite = { 409: ['document_trashed'], 422: ['invalid_body'] };
/** Every secret, or those in one state, paged (the ledger's list). */
export const secretsList = defineRoute({
    id: 'secrets.list',
    method: 'GET',
    path: '/api/secrets',
    query: SecretsQueryV1,
    response: { kind: 'json', schema: listOf(SecretV1) },
    reference: 'none',
    ...ledger,
    examples: {
        request: { query: { state: 'partial', limit: 50 } },
        response: { items: [EXAMPLE_SECRET], next: null },
    },
});
/** One document's secrets (a bounded list); `EditorServices.subscribeSecretState` reads it. */
export const secretsForDoc = defineRoute({
    id: 'secrets.forDoc',
    method: 'GET',
    path: '/api/docs/:id/secrets',
    params: DocIdParams,
    response: { kind: 'json', schema: listOf(SecretV1) },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...ledger,
    examples: {
        request: { params: { id: ids.npcMaelisDorn.id } },
        response: { items: [EXAMPLE_SECRET], next: null },
    },
});
/** Record what the table learned of a secret or a document. */
export const knowledgeCreate = defineRoute({
    id: 'knowledge.create',
    method: 'POST',
    path: '/api/knowledge-events',
    body: KnowledgeCreateV1,
    response: { kind: 'json', schema: KnowledgeEventV1, status: 201 },
    errors: ledgerWrite,
    reference: 'deny',
    docIds: { owner: ['body.documentId', 'body.sessionId', 'body.pcIds[]'] },
    ...ledger,
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_1M1_S3,
    examples: {
        request: {
            body: {
                documentId: ids.itemTidebell.id,
                state: 'known',
                sessionId: ids.sessionSaltInTheWells.id,
                pcIds: [ids.pcTamsinReed.id],
                how: 'Tamsin rang it on the Old Quay.',
            },
        },
        response: EXAMPLE_KNOWLEDGE_CREATED,
    },
});
/** Retract an event (an action on it, so 200): a `retract` row supersedes it in the fold. */
export const knowledgeRetract = defineRoute({
    id: 'knowledge.retract',
    method: 'POST',
    path: '/api/knowledge-events/:id/retract',
    params: IdParams,
    response: { kind: 'json', schema: KnowledgeEventV1 },
    errors: ledgerWrite,
    reference: 'none',
    ...ledger,
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_1M1_S3,
    examples: {
        request: { params: { id: ids.knowledgeS3Tentative.id } },
        response: EXAMPLE_KNOWLEDGE_RETRACTED,
        consumes: true,
    },
});
/** Record that a document, or one scene of it, was played. */
export const playCreate = defineRoute({
    id: 'play.create',
    method: 'POST',
    path: '/api/play-records',
    body: PlayCreateV1,
    response: { kind: 'json', schema: PlayRecordV1, status: 201 },
    errors: ledgerWrite,
    reference: 'deny',
    docIds: { owner: ['body.documentId', 'body.sessionId'] },
    ...ledger,
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_1M1_S3,
    examples: {
        request: {
            body: {
                documentId: ids.questSunkenChapel.id,
                sessionId: ids.sessionSunkenChapel.id,
                state: 'played',
                outcomeNote: 'The party bargained instead of fighting.',
            },
        },
        response: EXAMPLE_PLAY_CREATED,
    },
});
/** One document's play records (a bounded list), oldest first. */
export const playForDoc = defineRoute({
    id: 'play.forDoc',
    method: 'GET',
    path: '/api/docs/:id/play',
    params: DocIdParams,
    response: { kind: 'json', schema: listOf(PlayRecordV1) },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...ledger,
    examples: {
        request: { params: { id: ids.questMissingLamplighters.id } },
        response: { items: [EXAMPLE_PLAY_RECORD], next: null },
    },
});
/**
 * The sessions' refusals (CS r2c; 1.T items T1 and T4): a write the schema admits but the rows
 * refuse (a number another session holds, trashed or merged included; a status past `prepped`
 * without a number; `running`; a title the database cannot store) answers 422 `invalid_body` with
 * `details.issues` naming the path.
 */
const sessionWrite = { 422: ['invalid_body'] };
/**
 * A session document of the built-in `session` type, with its `session_meta` and an undated
 * placement with role `session` (1.T item T2).
 */
export const sessionsCreate = defineRoute({
    id: 'sessions.create',
    method: 'POST',
    path: '/api/sessions',
    body: SessionCreateV1,
    response: { kind: 'json', schema: SessionV1, status: 201 },
    errors: sessionWrite,
    reference: 'none',
    ...sessions,
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: { body: { title: "The Abbot's Bargain", status: 'planned' } },
        response: EXAMPLE_SESSION_CREATED,
    },
});
/** Every session, newest number first, paged. */
export const sessionsList = defineRoute({
    id: 'sessions.list',
    method: 'GET',
    path: '/api/sessions',
    query: PageQueryV1,
    response: { kind: 'json', schema: listOf(SessionV1) },
    reference: 'none',
    ...sessions,
    examples: {
        request: { query: { limit: 50 } },
        response: { items: [EXAMPLE_SESSION, EXAMPLE_SESSION_PLAYED], next: null },
    },
});
/**
 * One session. A reference document is never a session (migration 0010's guards keep
 * `session_meta` off reference documents), so the route is `deny` (CS r2c, 1.T item T3; a ruled
 * break, DECISIONS 139): a reference id answers 403 `reference_read_only` before the handler, and an
 * unknown id, a non-session document or a trashed or merged session 404 `not_found`.
 */
export const sessionsGet = defineRoute({
    id: 'sessions.get',
    method: 'GET',
    path: '/api/sessions/:id',
    params: DocIdParams,
    response: { kind: 'json', schema: SessionV1 },
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...sessions,
    examples: { request: { params: { id: ids.sessionSunkenChapel.id } }, response: EXAMPLE_SESSION },
});
/**
 * Change a session's number, real date or status (CS r2c, 1.T item T4; DECISIONS 139 1.T item 1):
 * no `baseRv`, the last write wins, and never to `running`. An unknown id, a non-session document
 * or a trashed or merged session answers 404 `not_found` as `sessions.get` does (no 409
 * `document_trashed`); a reference id 403 before the handler.
 */
export const sessionsUpdate = defineRoute({
    id: 'sessions.update',
    method: 'PATCH',
    path: '/api/sessions/:id',
    params: DocIdParams,
    body: SessionUpdateV1,
    response: { kind: 'json', schema: SessionV1 },
    errors: sessionWrite,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...sessions,
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2C_S3,
    examples: {
        request: { params: { id: ids.sessionSunkenChapel.id }, body: { realDate: '2026-09-19' } },
        response: EXAMPLE_SESSION_UPDATED,
        consumes: true,
    },
});
//# sourceMappingURL=ledger.js.map